import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Apartment as ApartmentIcon,
  AdminPanelSettings as AdminIcon,
  ReceiptLong as BillingIcon,
  Extension as ModuleIcon,
  History as AuditIcon,
  ListAlt as FieldRegistryIcon,
  Visibility as FeatureCardsIcon,
  Tune as DataSettingsIcon,
  Key as ApiClientsIcon,
  AccountTree as AtlasStructureIcon,
  BugReport as AtlasDebugIcon,
  Settings as ConfigIcon,
  Logout as LogoutIcon,
  ChevronLeft,
  ChevronRight,
  Person as PersonIcon,
} from '@mui/icons-material';

import { useAuthStore } from '../store/authStore';
import LanguageSelector from '../shared/components/LanguageSelector';
import { brand } from '../theme';
import beoLogo from '../assets/images/beo-logo.png';

interface AdminNavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  group: string;
}

const NAV: AdminNavItem[] = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: <DashboardIcon />, group: 'Overview' },
  { label: 'Tenants', path: '/admin/tenants', icon: <ApartmentIcon />, group: 'Tenancy' },
  { label: 'Super Admins', path: '/admin/super-admins', icon: <AdminIcon />, group: 'Tenancy' },
  { label: 'Billing', path: '/admin/billing', icon: <BillingIcon />, group: 'Tenancy' },
  { label: 'Modules', path: '/admin/modules', icon: <ModuleIcon />, group: 'System' },
  { label: 'Audit Logs', path: '/admin/audit-logs', icon: <AuditIcon />, group: 'System' },
  { label: 'Field Registry', path: '/admin/field-registry', icon: <FieldRegistryIcon />, group: 'System' },
  { label: 'Feature Cards', path: '/admin/feature-cards', icon: <FeatureCardsIcon />, group: 'System' },
  { label: 'Data Settings', path: '/admin/data-settings', icon: <DataSettingsIcon />, group: 'System' },
  { label: 'API Clients', path: '/admin/api-clients', icon: <ApiClientsIcon />, group: 'System' },
  { label: 'ATLAS Structure', path: '/admin/atlas-structure', icon: <AtlasStructureIcon />, group: 'System' },
  { label: 'ATLAS Debug', path: '/admin/atlas-debug', icon: <AtlasDebugIcon />, group: 'System' },
  { label: 'Configuration', path: '/admin/configuration', icon: <ConfigIcon />, group: 'Config' },
];

const GROUP_ORDER = ['Overview', 'Tenancy', 'System', 'Config'];
const EXPANDED = 240;
const COLLAPSED = 60;

const AdminShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('admin.sidebar.collapsed') === '1',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem('admin.sidebar.collapsed', c ? '0' : '1');
      return !c;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/admin/login');
    }
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header band — matches the app theme */}
      <Box
        component="header"
        sx={{
          height: 60,
          backgroundColor: brand.yellow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          borderBottom: `1px solid ${brand.borderSubtle}`,
          flexShrink: 0,
          zIndex: 1100,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={beoLogo}
            alt="BEO Software"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ height: 44, width: 'auto', cursor: 'pointer' }}
          />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: brand.textPrimary, display: { xs: 'none', sm: 'block' } }}>
            Admin Console
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageSelector />
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }} aria-label="Admin menu">
            <Avatar sx={{ width: 32, height: 32, bgcolor: brand.orange, color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>
              {user?.firstname?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{
            width: collapsed ? COLLAPSED : EXPANDED,
            flexShrink: 0,
            backgroundColor: brand.sidebarSurface,
            borderRight: `1px solid ${brand.borderSubtle}`,
            transition: 'width 0.18s ease',
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            py: 1,
          }}
        >
          {GROUP_ORDER.map((group) => {
            const items = NAV.filter((n) => n.group === group);
            if (!items.length) return null;
            return (
              <Box key={group} sx={{ mb: 0.5 }}>
                {!collapsed && (
                  <Typography variant="caption" sx={{ px: 2, py: 0.5, color: brand.textMuted, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.6, display: 'block' }}>
                    {group}
                  </Typography>
                )}
                {items.map((item) => {
                  const active = isActive(item.path);
                  const row = (
                    <Box
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mx: 1,
                        my: 0.25,
                        px: 1.25,
                        py: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        color: brand.textPrimary,
                        backgroundColor: active ? '#FFFFFF' : 'transparent',
                        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        '&:hover': { backgroundColor: active ? '#FFFFFF' : 'rgba(0,0,0,0.04)' },
                        '& svg': { fontSize: 20, color: active ? brand.orange : brand.textMuted },
                      }}
                    >
                      {item.icon}
                      {!collapsed && (
                        <Typography variant="body2" fontWeight={active ? 700 : 500} noWrap>
                          {item.label}
                        </Typography>
                      )}
                    </Box>
                  );
                  return collapsed ? (
                    <Tooltip key={item.path} title={item.label} placement="right">{row}</Tooltip>
                  ) : row;
                })}
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
          <Divider sx={{ my: 0.5 }} />
          <Box
            onClick={toggleCollapsed}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-end', px: 1.5, py: 1, cursor: 'pointer', color: brand.textMuted }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </Box>
        </Box>

        {/* Content */}
        <Box component="main" sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: { xs: 2, md: 3 }, backgroundColor: '#FAFAFA' }}>
          <Outlet />
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
          <PersonIcon sx={{ mr: 1, fontSize: 20 }} /> Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1, fontSize: 20 }} /> Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminShell;
